import BaseListStore from 'stores/BaseListStore';
import ControllerStore from 'stores/controller/ControllerStore';
import { SortDirection } from 'constants/SortDirection';
import { IDataFetchOptions } from '../../types';
import { getConditions, getListConditions } from 'stores/_services/filterService';
import { buildWhereFilter, destroy, getCount, getPage, post } from 'stores/_services/dataService';
import { runInAction } from 'mobx';

interface IGetControllersArgs extends IDataFetchOptions {
    controllerGroupId?: string;
}

const SEARCH_FIELDS = ['ipstring', 'hostname'];

export default class ControllerListStore extends BaseListStore<ControllerStore> {
    constructor() {
        super({
            itemStore: ControllerStore,
            sortBy: 'createdOn',
            sortDirection: SortDirection.DESC
        });
    }

    async getControllers({ ...args }: IGetControllersArgs = {}): Promise<void> {
        const shouldReset = this._updateListState(args);
        const conditions = getConditions(this.filter);

        const options = {
            filter: {
                limit: this.limit,
                skip: this.skip,
                searchFields: SEARCH_FIELDS,
                where: conditions,
                order: [this.sort, `hostname ${this.sortDirection}`]
            }
        };

        const { data } = await getPage('controllers', options);

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, this.skip);
        this._updateCount(data);
    }

    async getTotalCount(): Promise<void> {
        const totalCount = await getCount('controllers', { searchFields: SEARCH_FIELDS });
        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.totalCount = totalCount;
        });
    }

    async deleteControllers(itemId?: string): Promise<void> {
        const conditions = getListConditions(itemId ? [itemId] : this.selectedIdList, this.filter);

        const options = {
            where: conditions,
            searchFields: SEARCH_FIELDS
        };

        await destroy('controllers', options);

        this.deselectAll();
        this.getControllers();
        this.getTotalCount();
    }

    async enableControllers(itemId?: string): Promise<void> {
        return this.toggleControllers('approve', itemId);
    }

    async disableControllers(itemId?: string): Promise<void> {
        return this.toggleControllers('deny', itemId);
    }

    async toggleControllers(action: 'approve' | 'deny', itemId?: string): Promise<void> {
        const conditions = getListConditions(itemId ? [itemId] : this.selectedIdList, this.filter);
        const where = buildWhereFilter({
            where: conditions,
            searchFields: SEARCH_FIELDS
        });

        await post(`controllers/${action}`, { where });

        this.deselectAll();
        this.getControllers();
    }
}
